/* =====================================================================
 * @auw/i18n — useAppLocale hook.
 *
 * Phase 11-4c: the pre-split hook pulled `preferredLanguage` straight
 * out of `useUserStore` (a B2C-only Zustand store). Now the hook takes
 * the preferred language as an argument so the host can wire in
 * whatever store it uses — B2C still feeds it from `useUserStore`,
 * enterprise can feed it from its own profile slice, and tests can
 * pass a plain string.
 *
 * Side effects when `preferredLanguage` changes:
 *   - call `i18n.changeLanguage(appLocale)` if the new locale differs
 *   - set `document.documentElement.lang` so CSS :lang() selectors work
 *   - persist the app locale to tenant-scoped localStorage so an
 *     unauthenticated second visit still renders the right language
 * ===================================================================== */

import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { activeTenantKey } from '@auw/tenant';

import { resolveAppLocale, type AppLocale } from './resolveAppLocale';

export const LOCALE_STORAGE_KEY: string = activeTenantKey('app_locale');

export function useAppLocale(preferredLanguage?: string | null | undefined): AppLocale {
  const { i18n } = useTranslation();

  useEffect(() => {
    if (!preferredLanguage) {
      document.documentElement.lang = i18n.language;
      return;
    }

    const appLocale = resolveAppLocale(preferredLanguage);
    if (i18n.language !== appLocale) {
      void i18n.changeLanguage(appLocale);
    }
    document.documentElement.lang = appLocale;
    try {
      localStorage.setItem(LOCALE_STORAGE_KEY, appLocale);
    } catch {
      /* quota exceeded / disabled — not fatal, UI still renders. */
    }
  }, [preferredLanguage, i18n]);

  return i18n.language as AppLocale;
}
